import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

interface Tab {
  label: string;
  path: string;
  fullPath: string;  // Store full path including parameters and query params
  filterData: any;
  params?: any; // Added to support optional parameters
  quickFilter?: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class TabService {
  private storageKey = 'tabs';
  private tabsSubject: BehaviorSubject<Tab[]>;
  private activeTabSubject: BehaviorSubject<number>;

  tabs$: any;
  activeTab$: any;

  constructor() {
    const savedTabs = localStorage.getItem(this.storageKey);
    const initialTabs = savedTabs ? JSON.parse(savedTabs) : [];
    this.tabsSubject = new BehaviorSubject<Tab[]>(initialTabs);

    const savedActiveTab = localStorage.getItem(this.storageKey + '_active');
    const initialActiveTab = savedActiveTab ? Number(savedActiveTab) : 0;
    this.activeTabSubject = new BehaviorSubject<number>(initialActiveTab);

    this.tabs$ = this.tabsSubject.asObservable();
    this.activeTab$ = this.activeTabSubject.asObservable();
  }

  private saveTabs() {
    localStorage.setItem(this.storageKey, JSON.stringify(this.tabsSubject.value));
  }

  private saveActiveTab() {
    localStorage.setItem(this.storageKey + '_active', this.activeTabSubject.value.toString());
  }

  get tabs(): Tab[] {
    return this.tabsSubject.value;
  }

  get activeTab(): number {
    return this.activeTabSubject.value;
  }

  // TabService
private singletonPathPatterns = [
  'receiving-services/asn',
  'receiving-services/putaway',
  'receiving-services/grn',
  'order-services/picklist',
  'order-services/order',
  'order-services/manifest',
  'utility/upload',
  'ftl-master/quick-code',
  'orders/order-view',
  'orders/order-trip',
  'orders/end-trip',
];

addTab(tab: Tab) {
  const pathWithParams = tab.params
    ? `${tab.path}?${new URLSearchParams(tab.params).toString()}`
    : tab.path;

  tab.fullPath = pathWithParams;

  const existingTab = this.tabs.findIndex(t => {
    const matchedPattern = this.singletonPathPatterns.find(
      p => t?.path?.includes(p) && tab?.path?.includes(p)
    );
    if (matchedPattern) {
      t.fullPath = pathWithParams; // refresh id/params on the existing tab
      return true;
    }
    return t.fullPath === pathWithParams;
  });

  if (existingTab === -1) {
    this.tabsSubject.next([...this.tabs, tab]);
    this.setActiveTab(this.tabs.length);
  } else {
    this.setActiveTab(existingTab);
  }
  this.saveTabs();
  this.saveActiveTab();
}

  setActiveTab(index: number) {
    this.activeTabSubject.next(index);
    this.saveActiveTab();
  }

  updateTab(index: number, tab: Tab) {
    const tabs = [...this.tabs];
    if(tabs.length === index){
      index = tabs.length - 1
    }
    if (tabs.length > 0) {
      tabs[index] = tab;
      this.tabsSubject.next(tabs);
      this.saveTabs();
    }
  }

  closeTab(index: number) {
    const tabs = [...this.tabs];
    tabs.splice(index, 1);
    this.tabsSubject.next(tabs);
    this.saveTabs();
    if (this.activeTab === index) {
      if (tabs.length > 0) {
        this.setActiveTab(index === 0 ? 0 : index - 1);
      } else {
        this.setActiveTab(0);
      }
    } else if (this.activeTab > index) {
      this.setActiveTab(this.activeTab - 1);
    }
    this.saveActiveTab();
  }

  closeAllTab() {
    const tabs: Tab[] = [];
    this.tabsSubject.next(tabs);
    this.saveTabs();
  }

  updateFilterData(index: number, filterData: any) {
    const tabs = [...this.tabs];
    if(tabs.length === index){
      index = tabs.length - 1
    }
    if (tabs.length > 0) {
      tabs[index].filterData = filterData;
      this.tabsSubject.next(tabs);
      this.saveTabs();
    }
  }

  transformFiltersArrayToObject(filtersArray: any[]): any {
    return filtersArray.reduce((acc, curr) => {
      if (curr.value) {
        acc[curr.key] = curr.value;
      }
      if (curr.dateTimeValue) {
        acc[curr.key] = curr.dateTimeValue;
      }
      return acc;
    }, {});
  }

  activateTabForPath(path: string): number {
    const clean = path.split('?')[0];
    const index = this.tabs.findIndex(
      t => t.path === clean || t.fullPath.split('?')[0] === clean
    );
    if (index !== -1) {
      this.setActiveTab(index);
    }
    return index;
  }

  getQuickFilter(index?: number): string | null {
    const tabIndex = index ?? this.activeTab;
  
    if (tabIndex < 0 || tabIndex >= this.tabs.length) {
      return null;
    }
  
    return this.tabs[tabIndex]?.quickFilter ?? null;
  }
  
  updateQuickFilter(index: number, quickFilter: string | null): void {
    const tabs = [...this.tabs];
  
    if (index < 0 || index >= tabs.length) {
      return;
    }
  
    tabs[index] = {
      ...tabs[index],
      quickFilter
    };
  
    this.tabsSubject.next(tabs);
    this.saveTabs();
  }
}
